import { useEffect, useState } from "react";
import { Download, X, Share, Plus, Truck } from "lucide-react";

const DISMISS_KEY = "streateat_install_dismissed";
const DISMISS_DAYS = 7;

const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (iOS) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};

const recentlyDismissed = () => {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < DISMISS_DAYS * 864e5;
  } catch (e) { console.warn("install banner: read dismiss flag failed", e); return false; }
};

export const InstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("desktop");
  const [steps, setSteps] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const isMobile = window.matchMedia("(max-width: 768px)").matches && (p === "ios" || p === "android");
    if (standalone || recentlyDismissed() || !isMobile) return;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setVisible(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const t = setTimeout(() => setVisible(true), 4000);
    return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const dismiss = () => { try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { console.warn("install banner: persist dismiss failed", e); } setVisible(false); };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      if (outcome === "accepted") setVisible(false);
    } else {
      setSteps(true);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-3 inset-x-3 z-[70] lg:hidden animate-[slideUp_.35s_ease-out]" data-testid="install-banner">
      <div className="bg-white rounded-2xl shadow-warm-lg border border-cream-bg p-3.5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-street-gold/15 flex items-center justify-center shrink-0"><Truck size={22} strokeWidth={2.4} className="text-street-gold" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-urban-teal text-sm leading-tight">Installez l'app</p>
            <p className="text-xs text-urban-teal/60 leading-snug">Retrouvez vos foodtrucks en 1 tap.</p>
          </div>
          <button onClick={install} className="rounded-full bg-fresh-mint hover:bg-fresh-mint/90 text-white font-bold text-sm px-4 py-2 flex items-center gap-1 shrink-0" data-testid="install-banner-btn"><Download size={15} /> {platform === "ios" ? "Ajouter" : "Installer"}</button>
          <button onClick={dismiss} className="text-urban-teal/40 hover:text-urban-teal shrink-0 p-1" data-testid="install-banner-close" aria-label="Fermer"><X size={18} /></button>
        </div>
        {steps && (
          <div className="mt-3 pt-3 border-t border-cream-bg text-sm text-urban-teal/80" data-testid="install-banner-steps">
            {platform === "ios" ? (
              <p className="flex items-center gap-1.5 flex-wrap">Appuyez sur <Share size={15} className="text-street-gold" /> <b>Partager</b> puis <Plus size={15} className="text-street-gold" /> <b>Sur l'écran d'accueil</b>.</p>
            ) : (
              <p>Ouvrez le menu <b>⋮</b> de votre navigateur, puis <b>Installer l'application</b>.</p>
            )}
          </div>
        )}
      </div>
      <style>{`@keyframes slideUp{from{transform:translateY(120%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
};
